/**
 * Capitalization Exceptions Handler
 * Preserves specific capitalizations for brand names, proper nouns, and special terms
 * that should not be affected by CSS text-transform: capitalize
 *
 * Performance optimized version:
 * - Uses requestIdleCallback for non-blocking processing
 * - Skips processing during page updates (.is-loading)
 * - Debounced MutationObserver (150ms)
 * - Batch processing (20 elements at a time)
 * - Specific selectors only (no catch-all patterns)
 */

(function() {
  'use strict';

  class CapitalizeExceptions {
    constructor() {
      this.exceptions = this.parseExceptions();
      this.processedNodes = new WeakSet();

      if (this.exceptions.length > 0) {
        this.init();
      }
    }

    /**
     * Parse exceptions from data attribute on html element
     * @returns {Array} Array of exception objects with original and lowercase versions
     */
    parseExceptions() {
      const htmlElement = document.documentElement;
      const exceptionsData = htmlElement.getAttribute('data-capitalize-exceptions');

      if (!exceptionsData) {
        return [];
      }

      return exceptionsData
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(exception => ({
          original: exception,
          lowercase: exception.toLowerCase()
        }));
    }

    /**
     * Escape special regex characters
     * @param {string} str - String to escape
     * @returns {string} Escaped string
     */
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Initialize the exception handler
     */
    init() {
      // Process initial content during idle time
      this.processDocument();

      // Watch for dynamic content changes
      this.observeChanges();
    }

    /**
     * Process all text nodes in the document
     * Uses requestIdleCallback and batching for non-blocking performance
     */
    processDocument() {
      const processInBatches = () => {
        // Use specific selectors only - avoid broad patterns
        const selectors = [
          '.product-item__title',
          '.product__title',
          '.collection__title',
          '.card__heading'
        ];

        // Collect all elements
        const allElements = [];
        selectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          allElements.push(...elements);
        });

        // Process in small batches to avoid blocking
        const batchSize = 20;
        for (let i = 0; i < allElements.length; i += batchSize) {
          const batch = allElements.slice(i, i + batchSize);

          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              batch.forEach(element => this.processElement(element));
            }, { timeout: 1000 });
          } else {
            // Fallback: use setTimeout to avoid blocking
            setTimeout(() => {
              batch.forEach(element => this.processElement(element));
            }, 0);
          }
        }
      };

      // Delay initial processing to not interfere with page load
      if ('requestIdleCallback' in window) {
        requestIdleCallback(processInBatches, { timeout: 2000 });
      } else {
        setTimeout(processInBatches, 100);
      }
    }

    /**
     * Process a single element and its text nodes
     * @param {HTMLElement} element - Element to process
     */
    processElement(element) {
      if (this.processedNodes.has(element)) {
        return;
      }

      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      const nodesToProcess = [];
      let node;

      while (node = walker.nextNode()) {
        if (node.nodeValue && node.nodeValue.trim().length > 0) {
          nodesToProcess.push(node);
        }
      }

      nodesToProcess.forEach(textNode => {
        this.processTextNode(textNode);
      });

      this.processedNodes.add(element);
    }

    /**
     * Process a single text node, replacing exceptions with span elements
     * @param {Text} textNode - Text node to process
     */
    processTextNode(textNode) {
      const text = textNode.nodeValue;
      let hasMatch = false;

      // Quick check if any exceptions are present
      this.exceptions.forEach(exception => {
        if (text.toLowerCase().includes(exception.lowercase)) {
          hasMatch = true;
        }
      });

      if (!hasMatch) {
        return;
      }

      // Build document fragment with wrapped exceptions
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      // Find all exception matches with their positions
      const matches = [];
      this.exceptions.forEach(exception => {
        const regex = new RegExp(this.escapeRegex(exception.lowercase), 'gi');
        let match;
        while ((match = regex.exec(text.toLowerCase())) !== null) {
          matches.push({
            index: match.index,
            length: match[0].length,
            original: exception.original
          });
        }
      });

      // Sort matches by position
      matches.sort((a, b) => a.index - b.index);

      // Remove overlapping matches (keep first occurrence)
      const filteredMatches = [];
      let lastEnd = -1;
      matches.forEach(match => {
        if (match.index >= lastEnd) {
          filteredMatches.push(match);
          lastEnd = match.index + match.length;
        }
      });

      // Build the fragment with text nodes and span elements
      filteredMatches.forEach(match => {
        // Add text before the match
        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
        }

        // Add span with the exception text
        const span = document.createElement('span');
        span.style.textTransform = 'none';
        span.textContent = match.original;
        fragment.appendChild(span);

        lastIndex = match.index + match.length;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
      }

      // Replace the text node with the fragment
      textNode.parentNode.replaceChild(fragment, textNode);
    }

    /**
     * Set up MutationObserver to watch for dynamic content
     * Optimized with debouncing and skip during updates
     */
    observeChanges() {
      let processingTimeout;

      const observer = new MutationObserver((mutations) => {
        // Skip processing during collection/product updates to prevent blocking
        const isUpdating = document.querySelector('.is-loading, [data-products-grid].is-loading');
        if (isUpdating) {
          return;
        }

        // Clear existing timeout for debouncing
        if (processingTimeout) {
          clearTimeout(processingTimeout);
        }

        // Debounce processing by 150ms to batch changes
        processingTimeout = setTimeout(() => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Use requestIdleCallback to process during browser idle time
                  if ('requestIdleCallback' in window) {
                    requestIdleCallback(() => {
                      this.processElement(node);
                    });
                  } else {
                    // Fallback: use requestAnimationFrame
                    requestAnimationFrame(() => {
                      this.processElement(node);
                    });
                  }
                }
              });
            }
          });
        }, 150);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
        // Note: characterData observation removed - too expensive
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CapitalizeExceptions();
    });
  } else {
    new CapitalizeExceptions();
  }
})();
